import { AdasBox, AdasContainer, AdsCustomModal } from '@components/wrapper-components'
import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Clock, Calendar, PenLine, Link, Check } from 'lucide-react'
import ScheduleAvailability from '@components/calendar-settings/schedule-availability/ScheduleAvailability'
import DateOverrides from '@components/calendar-settings/date-overrides/DateOverrides'
import CalendarPreferences from '@components/calendar-settings/calendar-preferences/CalendarPreferences'
import BookingLink from './BookingLink'
import { Logger } from '../../logger'
import { getOrganisationByReferenceId, setupOrganisationByCCId } from '../../api/api'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { useCenterStore, useOrganisationStore, useLoadingStore, useMessageStore } from '@caradasstore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useUserStore } from '@caradasstore/UserStore'
import { SchedulerSetupPrompt } from '@components/scheduler'

const TABS = [
  { label: 'Hours', value: 'schedule-availability', icon: Clock },
  { label: 'Overrides', value: 'date-overrides', icon: Calendar },
  { label: 'Preferences', value: 'calendar-preferences', icon: PenLine }
]

export const CalendarPage = () => {
  const [calendarType, setCalendarType] = useState('schedule-availability')
  const [bookingLinkOpen, setBookingLinkOpen] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [nextLocation, setNextLocation] = useState(null)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
  const [showOrganisationSetup, setShowOrganisationSetup] = useState(false)
  const [showDefaultPage, setShowDefaultPage] = useState(true)
  const [pendingTabChange, setPendingTabChange] = useState(null)
  const [saveAction, setSaveAction] = useState({ onSave: null, disabled: true })
  const addMessage = useMessageStore((store) => store.addMessage)
  const currentUser = useUserStore((store) => store.currentUser)

  const history = useHistory()
  const unblockRef = useRef(null)

  const ccId = useCenterStore((store) => store.ccId)
  const setOrganisationDetail = useOrganisationStore((store) => store.setOrganisationDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)

  useEffect(() => {
    if (ccId) fetchOrganisationDetailsByCcId()
  }, [ccId, setOrganisationDetail, setShowOrganisationSetup, setShowDefaultPage])

  // Surface the booking link as the mobile header action (replaces search/filter on
  // this screen); the in-page "View Booking Link" button is desktop-only.
  useEffect(() => {
    if (showDefaultPage) {
      clearPageAction()
      return undefined
    }
    setPageAction({ label: 'View Booking Link', icon: Link, onClick: () => setBookingLinkOpen(true) })
    return () => clearPageAction()
  }, [showDefaultPage, setPageAction, clearPageAction])

  const fetchOrganisationDetailsByCcId = async () => {
    setLoading(true)
    const orgResponse = await getOrganisationByReferenceId({ ccId })
    setLoading(false)
    if (orgResponse?.status === 200) {
      if (orgResponse?.data.id) {
        setOrganisationDetail(orgResponse.data)
        setShowDefaultPage(false)
      } else if (orgResponse?.data?.id === null) {
        setShowOrganisationSetup(true)
      }
    } else {
      Logger.error({
        message: 'Failed to fetch organisation data',
        payload: { file: 'calendarpage', method: 'getOrganisationByReferenceId' }
      })
    }
  }
  const handleUnsavedChangesConfirm = ({ type }) => {
    if (type === 'confirm') {
      setHasUnsavedChanges(false)
      setOpenConfirmationDialog(false)
      setPendingTabChange(null)
      if (unblockRef.current) unblockRef.current()
      if (pendingTabChange) setCalendarType(pendingTabChange)
      if (nextLocation) {
        history.push(nextLocation.pathname)
        setNextLocation(null)
      }
    } else if (type === 'close') {
      setOpenConfirmationDialog(false)
    }
  }

  // CAR-3634: one friendly, mapped message — never raw server text.
  const handleCustomError = (error) => {
    addMessage({ text: getFriendlyErrorMessage(error) })
  }

  const handleSetupOrganisationConfirm = async () => {
    try {
      setLoading(true)
      const res = await setupOrganisationByCCId(ccId)
      if (res?.status === 200) {
        setOrganisationDetail(res.data)
        setShowDefaultPage(false)
      }
    } catch (error) {
      Logger.error({ message: 'Failed to setup organisation', payload: { file: 'calendarpage', method: 'setupOrganisationByCCId' } })
      handleCustomError(error)
    } finally {
      setLoading(false)
    }
  }

  const onTabChange = (value) => {
    if (hasUnsavedChanges) {
      setPendingTabChange(value)
      setOpenConfirmationDialog(true)
    } else {
      setCalendarType(value)
    }
  }

  const handleSaveClick = () => {
    if (saveAction.onSave) saveAction.onSave()
  }

  return (
    <AdasContainer maxWidth={false} className='pb-[10px]'>
      {!showDefaultPage && (
        <>
          <AdasBox className='pt-6 md:pt-4 mb-5 flex flex-wrap items-start justify-between gap-3'>
            {/* Back link + title are redundant on mobile — the global header already
                shows "Calendar Settings". Desktop only. */}
            <div className='max-[600px]:hidden'>
              <button
                type='button'
                onClick={() => history.goBack()}
                className='mb-1 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                Back to Calendar
              </button>
              <h1 className='text-[32px] font-bold leading-tight tracking-tight text-[#0A1628]'>Calendar Settings</h1>
            </div>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={() => setBookingLinkOpen(true)}
                className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC] max-[600px]:hidden'
              >
                <Link className='h-4 w-4' />
                View Booking Link
              </button>
              <button
                type='button'
                onClick={handleSaveClick}
                disabled={saveAction.disabled}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed max-[600px]:hidden'
              >
                Save Changes
              </button>
            </div>
          </AdasBox>

          <div className='mb-5 flex items-center border-b border-[#EBEDF0]'>
            {TABS.map((tab) => {
              const Icon = tab.icon
              const active = calendarType === tab.value
              return (
                <button
                  key={tab.value}
                  type='button'
                  onClick={() => onTabChange(tab.value)}
                  className={`-mb-px inline-flex flex-1 items-center justify-center gap-2 border-b-2 pb-3 text-[14px] transition-colors ${
                    active
                      ? 'border-[#1F6FEB] font-bold text-[#0A1628]'
                      : 'border-transparent font-medium text-[#6B7280] hover:text-[#334155]'
                  }`}
                >
                  <Icon className='h-4 w-4' />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {calendarType === 'schedule-availability' && (
            <ScheduleAvailability
              hasUnsavedChanges={hasUnsavedChanges}
              setHasUnsavedChanges={setHasUnsavedChanges}
              onRegisterSave={(action) => setSaveAction(action)}
            />
          )}
          {calendarType === 'date-overrides' && <DateOverrides />}
          {calendarType === 'calendar-preferences' && (
            <CalendarPreferences
              setBookingLinkOpen={setBookingLinkOpen}
              onSuccess={fetchOrganisationDetailsByCcId}
              onRegisterSave={(action) => setSaveAction(action)}
            />
          )}

          {/* Floating Save on mobile — mirrors the scheduler's "New" FAB. */}
          <button
            type='button'
            onClick={handleSaveClick}
            disabled={saveAction.disabled}
            className='fixed bottom-6 right-5 z-30 inline-flex items-center gap-2 rounded-full bg-[#1F2D3D] px-6 py-3.5 text-[15px] font-bold text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 min-[601px]:hidden'
          >
            <Check className='h-5 w-5' />
            Save
          </button>
        </>
      )}

      {showDefaultPage && (
        <AdasBox
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          gap={2}
          className='overflow-hidden min-h-[81vh]'
        >
          {showOrganisationSetup && <SchedulerSetupPrompt currentUser={currentUser} onSetup={handleSetupOrganisationConfirm} />}
        </AdasBox>
      )}

      {bookingLinkOpen && <BookingLink open={bookingLinkOpen} onClose={() => setBookingLinkOpen(false)} />}

      {openConfirmationDialog && (
        <AdsCustomModal
          title='Unsaved Changes'
          open={openConfirmationDialog}
          onClose={handleUnsavedChangesConfirm}
          description='You have unsaved changes. Do you want to leave without saving?'
          successButtonTitle='Leave'
        />
      )}
    </AdasContainer>
  )
}
